import { useEffect, useState } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import { Plus, Wallet } from 'lucide-react'
import { getVendors } from '../../api/master'
import { getVendorProductHistory, listVendorOpenInvoices } from '../../api/purchase'
import Spinner from '../../components/Spinner'
import { BackLink, DocHeader } from '../../components/PageParts'
import { fmtDay, parseWallClock, startOfToday } from '../../utils/format'
import { docNo } from '../../utils/docNumbers'
import { money, num } from '../sales/salesShared'

// A vendor: who they are, what you owe them, and what you buy from them.
export default function APVendorDetail() {
  const { vendorId } = useParams()
  const location = useLocation()
  const viaAccounting = location.pathname.startsWith('/accounting/')
  const payPath = `${viaAccounting ? '/accounting/ap/payment' : '/purchase/ap-ledger'}?vendor_id=${vendorId}`
  const [vendor, setVendor] = useState(null)
  const [bills, setBills] = useState([])
  const [history, setHistory] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    Promise.all([
      getVendors().then((vs) => setVendor((vs || []).find((v) => String(v.vendor_id) === String(vendorId)) || null)).catch(() => {}),
      listVendorOpenInvoices(vendorId).then((r) => setBills(r || [])).catch(() => setBills([])),
      getVendorProductHistory(vendorId).then((r) => setHistory(r || [])).catch(() => setHistory([])),
    ]).finally(() => setLoading(false))
  }, [vendorId])

  const owed = bills.reduce((s, b) => s + num(b.balance), 0)
  const today = startOfToday()
  const contact = vendor && [
    vendor.contact_name,
    vendor.phone,
    vendor.email && <a key="email" href={`mailto:${vendor.email}`}>{vendor.email}</a>,
    [vendor.address, vendor.city, [vendor.state, vendor.postal_code].filter(Boolean).join(' '), vendor.country].filter(Boolean).join(', '),
  ].filter(Boolean)

  return (
    <div className="ci-form-page">
      <DocHeader
        back={viaAccounting ? <BackLink to="/accounting/reports/ap-subledger" label="AP subledger" /> : <BackLink to="/master/vendors" label="Vendors" />}
        title={vendor?.name || `Vendor #${vendorId}`}
        meta={contact?.length ? contact.map((c, i) => <span key={i}>{i > 0 && ' · '}{c}</span>) : null}
        actions={(
          <>
            {!viaAccounting && <Link data-write className="ci-btn ci-btn-ghost" to="/purchase/po-form/new"><Plus size={14} />New purchase order</Link>}
            {owed > 0 && <Link data-write className="ci-btn ci-btn-primary" to={payPath}><Wallet size={14} />Pay {money(owed)}</Link>}
          </>
        )}
      />

      {loading ? <Spinner label="Loading vendor…" /> : (
        <>
          <section className="doc-card">
            <div className="doc-card-head">
              <h2>Unpaid bills</h2>
              <span>{bills.length ? `${money(owed)} owed` : 'Nothing owed'}</span>
            </div>
            {bills.length ? (
              <div className="doc-table-scroll">
                <table className="ci-line-table">
                  <thead><tr><th>Bill</th><th>Date</th><th>Due</th><th className="num">Total</th><th className="num">Still owed</th><th /></tr></thead>
                  <tbody>
                    {bills.map((b) => {
                      const due = parseWallClock(b.due_date)
                      const overdue = due && due < today
                      return (
                        <tr key={b.invoice_id}>
                          <td>{b.invoice_number || docNo('bill', b.bill_no)}</td>
                          <td>{fmtDay(b.invoice_date) || '–'}</td>
                          <td className={overdue ? 'ci-overdue-cell' : undefined}>{fmtDay(b.due_date) || '–'}{overdue ? ' · overdue' : ''}</td>
                          <td className="num">{money(b.total_amount)}</td>
                          <td className="num ci-total">{money(b.balance)}</td>
                          <td className="num"><Link data-write className="ci-btn ci-btn-ghost ci-btn-sm" to={`${payPath}&invoice_id=${b.invoice_id}`}>Pay</Link></td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="doc-empty">You don’t owe this vendor anything.</p>
            )}
          </section>

          <section className="doc-card">
            <div className="doc-card-head">
              <h2>What you buy from them</h2>
              <span>{history.length} {history.length === 1 ? 'product' : 'products'}</span>
            </div>
            {history.length ? (
              <div className="doc-table-scroll">
                <table className="ci-line-table">
                  <thead><tr><th>Product</th><th className="num">Ordered</th><th className="num">Received</th><th className="num">Average cost</th><th>Last ordered</th></tr></thead>
                  <tbody>
                    {history.map((h, i) => (
                      <tr key={`${h.sku}-${i}`}>
                        <td>{h.name || h.sku}<span className="cell-sub">{h.sku}</span></td>
                        <td className="num">{num(h.ordered_qty).toLocaleString()}</td>
                        <td className="num">{num(h.received_qty).toLocaleString()}</td>
                        <td className="num">{money(h.avg_unit_cost)}</td>
                        <td>{fmtDay(h.last_order_date) || '–'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="doc-empty">Nothing has been ordered from this vendor yet.</p>
            )}
          </section>
        </>
      )}
    </div>
  )
}
